import {
  useCliente,
  useClientiList,
  useCreateCliente,
  usePartialUpdateCliente,
} from '../../hooks/clienti'
import { useQsFilters } from '../../hooks/filters'
import { Button } from 'react-bootstrap'
import Paginator from '../../components/Paginator'
import { Cliente, Sede } from '../../types'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import ModalForm from '../../components/ModalForm'
import FormCliente from '../../components/FormCliente'
import * as yup from 'yup'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { PartialWithId } from '@inmagik/react-crud/dist/types'
import { FormikHelpers } from 'formik'
import { Link } from 'react-router-dom'
import StickyTable from '../../components/StickyTable'
import Sorter, { SortControl } from '../../components/Sorter'
import ModalConfirm from '../../components/ModalConfirm'
import { toast } from 'react-toastify'
import { orderBy } from 'lodash'
import { useProvinceList } from '../../hooks/province'
import { omit } from 'lodash'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? 'ragione_sociale',
  luogo: params.get('luogo') ?? '',
})

const initialValues = {
  ragione_sociale: '',
  ente_pubblico: false,
  sedi_write: [
    {
      via: '',
      civico: '',
      nazione: 'ITALIA',
      partita_iva: '',
      numero_rea: '',
      nome_referente: '',
      email_referente: '',
      comune: undefined,
      provincia: undefined,
      toponimo: undefined,
      telefono: '',
      sede_principale: true,
    },
  ] as Sede[],
}

export default function Clienti() {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const clienti = useClientiList({
    search: filters.search,
    ordering: filters.ordering,
    page: String(filters.page),
    luogo: filters.luogo,
  })

  const { mutateAsync: updatePartialCliente } = usePartialUpdateCliente()

  const [modalStateForm, modalActionsForm] = useModalTrigger<Cliente>()
  const [modalUpdateState, modalUpdateActions] = useModalTrigger<Cliente>()
  const [modalDeleteState, modalDeleteActions] = useModalTrigger<Cliente>()

  const cliente = useCliente(
    String(modalUpdateState.value?.id),
    modalUpdateState.isOpen
  )

  const { mutateAsync: createCliente } = useCreateCliente()

  const saveCliente = useCallback(
    (cliente: PartialWithId<Cliente>, actions: FormikHelpers<Cliente>) => {
      return createCliente(cliente).then(() => {
        modalActionsForm.close()
        actions.resetForm()
      })
    },
    [createCliente, modalActionsForm]
  )

  const [openMoreCliente, setOpenMoreCliente] = useState<number | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        e.target instanceof Element &&
        !e.target.closest(`#cliente-${openMoreCliente}`)
      ) {
        setOpenMoreCliente(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [openMoreCliente])

  const province = useProvinceList()

  const provinceCodes = useMemo(() => {
    return province?.data?.map((p) => p.sigla)
  }, [province])

  const schema = useMemo(() => {
    return yup.object().shape({
      ragione_sociale: yup.string().required().label('Ragione sociale'),
      sedi_write: yup.array().of(
        yup.object().shape({
          toponimo: yup.number().required().label('Toponimo'),
          via: yup.string().required().label('Via'),
          comune: yup.string().required().label('Comune'),
          provincia: yup.string().required().label('Provincia'),
          nazione: yup.string().required().label('Nazione'),
          email_referente: yup.string().email().label('Email referente'),
          codice_fiscale: yup
            .string()
            .label('Codice fiscale')
            .when('$ente_pubblico', (ente_pubblico: any, schema: any) => {
              if (ente_pubblico[0] === 'true' || ente_pubblico[0] === true) {
                return schema.required()
              } else {
                return schema.notRequired()
              }
            }),
          numero_rea: yup.string().label('Numero Rea').when('$ente_pubblico', {
            is: (ente_pubblico: any) =>
              ente_pubblico === false || ente_pubblico === 'false',
            then: (schema) =>
              schema
                .required()
                .test(
                  'valid-numero-rea',
                  'Numero REA non valido',
                  function (value: any) {
                    if (!value || value.length < 3) return false // Ensure there's a value and it's long enough
                    const prefix = value.slice(0, 2) // Get the first two characters
                    const numbers = value.slice(2) // Get the rest of the string
                    return (
                      provinceCodes?.includes(prefix) && /^\d+$/.test(numbers)
                    ) // Check if prefix is valid and the rest are digits
                  }
                ),
            otherwise: (schema) => schema.notRequired(),
          }),
        })
      ),
    })
  }, [provinceCodes])

  return (
    <div className="page">
      <div className="row mt-4">
        <div className="col-12 d-flex justify-content-between">
          <h1 className="title">Clienti</h1>
          <div>
            <Button
              onClick={() => {
                modalActionsForm.open()
              }}
              variant="secondary"
              className="text-white d-flex align-items-center"
              type="button"
            >
              <span>Crea cliente</span>
              <span className="material-icons ms-2">add</span>
            </Button>
          </div>
        </div>
        <div className="col-12">
          <div className="flex-1 d-flex flex-column overflow-hidden">
            <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded">
              <div className="d-flex align-items-center">
                <div className="ms-2 position-relative">
                  <input
                    type="text"
                    style={{ width: '300px', paddingLeft: '40px' }}
                    className="form-control"
                    placeholder="Cerca cliente..."
                    value={uiFilters.search}
                    onChange={(e) =>
                      setFilters({ search: e.target.value, page: 1 })
                    }
                  />
                  <span
                    className="position-absolute material-icons"
                    style={{ left: '10px', top: '8px' }}
                  >
                    search
                  </span>
                </div>
                <div className="ms-2 position-relative">
                  <input
                    type="text"
                    style={{ width: '300px', paddingLeft: '40px' }}
                    className="form-control"
                    placeholder="Cerca luogo..."
                    value={uiFilters.luogo}
                    onChange={(e) =>
                      setFilters({ ...filters, luogo: e.target.value, page: 1 })
                    }
                  />
                  <span
                    className="position-absolute material-icons"
                    style={{ left: '10px', top: '8px' }}
                  >
                    search
                  </span>
                </div>
              </div>
              <div className="me-2">
                Stai visualizzando {clienti?.data?.count ?? 0} di{' '}
                {clienti?.data?.full_count ?? 0} clienti
              </div>
            </div>
            <StickyTable isLoading={clienti?.isLoading}>
              <thead className="position-sticky top-0 bg-white">
                <Sorter
                  value={uiFilters.ordering}
                  onSortChange={(ordering) =>
                    setFilters({
                      ordering,
                    })
                  }
                >
                  <tr>
                    <th style={{ width: '40%' }}>
                      <div className="d-flex align-items-center">
                        Ragione sociale
                        <SortControl field="ragione_sociale" />
                      </div>
                    </th>

                    <th style={{ width: '20%' }}>
                      <div className="d-flex align-items-center">
                        Indirizzo
                        <SortControl field="sede_principale__via" />
                      </div>
                    </th>
                    <th style={{ width: '20%' }}>
                      <div className="d-flex align-items-center">
                        Sede
                        <SortControl field="sede_principale__comune" />
                      </div>
                    </th>
                    <th style={{ width: '15%' }}>
                      <div className="d-flex align-items-center">
                        Partita iva
                        <SortControl field="sede_principale__partita_iva" />
                      </div>
                    </th>
                    <th style={{ width: '5%' }}>
                      <div className="d-flex align-items-center">
                        Sedi
                        <SortControl field="numero_sedi" />
                      </div>
                    </th>
                  </tr>
                </Sorter>
              </thead>
              <tbody>
                {clienti?.data?.count === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center">
                      Nessun cliente trovato
                    </td>
                  </tr>
                )}
                {clienti?.data?.results?.map((cliente) => (
                  <tr key={cliente.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div>
                          <Link
                            className={'link-md'}
                            to={`/clienti/${cliente.id}`}
                          >
                            {cliente.ragione_sociale}
                          </Link>
                          <div className="p-md text-body-secondary">
                            {cliente.sede_principale?.numero_rea}
                          </div>
                        </div>
                        <div>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (openMoreCliente === cliente.id) {
                                setOpenMoreCliente(null)
                              } else {
                                setOpenMoreCliente(cliente.id)
                              }
                            }}
                            variant="light"
                            className="text-secondary d-flex align-items-center ms-4"
                            type="button"
                          >
                            <span className="material-icons">more_horiz</span>
                          </Button>
                          {openMoreCliente === cliente.id && (
                            <div
                              id={`cliente-${cliente.id}`}
                              className="list-group position-absolute"
                              // ref={editDeleteRef}
                              style={{
                                // zIndex: 1,
                                width: '200px',
                                boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                                borderRadius: '5px',
                              }}
                            >
                              <button
                                onClick={() => {
                                  modalUpdateActions?.open(cliente)
                                }}
                                className="list-group-item list-group-item-action d-flex align-items-center"
                              >
                                <span
                                  className="material-icons me-2"
                                  style={{
                                    fontSize: '16px',
                                  }}
                                >
                                  edit
                                </span>
                                Modifica
                              </button>
                              <button
                                onClick={() => {
                                  modalDeleteActions?.open(cliente)
                                }}
                                className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                              >
                                <span
                                  className="material-icons me-2"
                                  style={{
                                    fontSize: '16px',
                                  }}
                                >
                                  delete
                                </span>
                                Elimina
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>
                      {cliente.sede_principale?.toponimo_data?.nome}{' '}
                      {cliente.sede_principale?.via}
                      {', '} {cliente.sede_principale?.civico}
                    </td>
                    <td>
                      <small>{cliente.sede_principale?.nazione}</small>
                      <br />
                      {cliente.sede_principale?.comune_data?.nome} (
                      {cliente.sede_principale?.provincia_data?.sigla})
                    </td>
                    <td>{cliente.sede_principale?.partita_iva ?? ''}</td>
                    <td>{cliente.numero_sedi}</td>
                  </tr>
                ))}
              </tbody>
            </StickyTable>
            <div className="position-relative">
              <Paginator
                count={clienti?.data?.count ?? 0}
                currentPage={uiFilters.page}
                goToPage={(page) => setFilters({ page })}
              />
            </div>
          </div>
        </div>
      </div>
      {modalStateForm.isOpen && (
        <ModalForm<Cliente>
          title={'Crea cliente'}
          isOpen={modalStateForm.isOpen}
          toggle={modalActionsForm.toggle}
          initialValues={modalStateForm.value ?? initialValues}
          save={(saveCliente)}
          schema={schema}
        >
          <FormCliente />
        </ModalForm>
      )}
      {modalDeleteState.isOpen && (
        <ModalConfirm
          isOpen={modalDeleteState.isOpen}
          toggle={modalDeleteActions.toggle}
          title="Elimina cliente"
          confirmText="Elimina"
          body={
            'Sei sicuro di voler eliminare il cliente ' +
            modalDeleteState.value?.ragione_sociale +
            '?'
          }
          action={async () => {
            await updatePartialCliente({
              id: modalDeleteState.value?.id,
              cancellato: true,
            })
            modalDeleteActions.close()
            toast.success('Cliente eliminato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          }}
        />
      )}
      {modalUpdateState.isOpen && (
        <ModalForm<Cliente>
          isOpen={modalUpdateState.isOpen}
          toggle={modalUpdateActions.close}
          title="Modifica cliente"
          initialValues={
            cliente?.data
              ? {
                ...omit(cliente?.data, "sedi_data"),
                  sedi_write:
                    orderBy(
                      cliente.data.sedi_data,
                      'sede_principale',
                      'desc'
                    ) ?? [],
                }
              : initialValues
          }
          schema={schema}
          save={async (values, actions) => {
            // validate values passing ente_pubblico to the schema
            
            await updatePartialCliente({
              id: values.id,
              ...values,
            })
            modalUpdateActions.close()
            toast.success('Cliente modificato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
          }}
          labelSave="Salva"
        >
          <FormCliente />
        </ModalForm>
      )}
    </div>
  )
}
